import React from 'react'
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom'

import { WithOptimizelyProps, withOptimizely } from '@optimizely/react-sdk'

import Analyzing from 'pages/Analyzing'
import BankLinkSuccess from 'pages/BankLinkSuccess'
import CardOrderReview from 'pages/CardOrderReview'
import SignaturePhone from 'pages/CardSignature/SignaturePhone'
import SignYourCard from 'pages/CardSignature/SignYourCard'
import ChooseFundingSource from 'pages/ChooseFundingSource'
import ContactInfo from 'pages/ContactInfo'
import DirectToMobileLander from 'pages/DirectToMobileLander'
import Funding from 'pages/Funding'
import Institutions from 'pages/Institutions'
import {
  DirectBankIntegration,
  DirectBankIntegrationRedirect,
} from 'pages/Institutions/DirectIntegration'
import InvestInterstitial from 'pages/InvestInterstitial'
import InvestorQuestions from 'pages/InvestorQuestions'
import LaterIraRecommendation from 'pages/LaterIraRecommendation'
import LaterSuitabilityQuestions from 'pages/LaterSuitabilityQuestions'
import Maintenance from 'pages/Maintenance'
import ManualFundingSource from 'pages/ManualFundingSource'
import MFASetup from 'pages/MFASetup'
import MOCInterstitial from 'pages/MOC/Interstitial'
import MOCProgressStepOne from 'pages/MOC/ProgressStepOne'
import MOCProgressStepThree from 'pages/MOC/ProgressStepThree'
import MOCProgressStepTwo from 'pages/MOC/ProgressStepTwo'
import MOCReferralReminder from 'pages/MOC/ReferralReminder'
import Occupation from 'pages/Occupation'
import PersonalInfo from 'pages/PersonalInfo'
import PlaidLinkRedirect from 'pages/PlaidLinkRedirect'
import PortfolioOverview from 'pages/PortfolioOverview'
import PortfolioRecommendation from 'pages/PortfolioRecommendation'
import AboutPortfolio from 'pages/PortfolioTheme/AboutPortfolio'
import AboutESG from 'pages/PortfolioTheme/AboutSustainability'
import ESGEducationFlow from 'pages/PortfolioTheme/EducationTour'
import PreviewPortfolio from 'pages/PortfolioTheme/preview-portfolio'
import CryptoInterstitial from 'pages/PortfolioTheme/preview-portfolio/Crypto'
import SelectPortfolioTheme from 'pages/PortfolioTheme/select-portfolio'
import Prefund from 'pages/Prefund'
import PricingInterstitial from 'pages/PricingInterstitial'
import ReferralDisclosure from 'pages/ReferralDisclosure'
import ReferralReward from 'pages/ReferralReward'
import RoundUpsFunding from 'pages/RoundUpsFunding'
import RoundUpsInterstitial from 'pages/RoundUpsInterstitial'
import RoundUpsPast from 'pages/RoundUpsPast'
import RoundUpsSetup from 'pages/RoundUpsSetup'
import SecondChanceBankLink from 'pages/SecondChanceBankLink'
import SelectTier from 'pages/SelectTier'
import ShippingAddress from 'pages/ShippingAddress'
import Signup from 'pages/Signup'
import SpendCoreInterstitial from 'pages/SpendCoreInterstitial'
import SpendOrderConfirmation from 'pages/SpendOrderConfirmation'
import SSN from 'pages/SSN'
import OneTimeInvestment from 'pages/StartInvesting/OneTime'
import RecurringInvestment from 'pages/StartInvesting/Recurring'
import Success from 'pages/Success'
import SuitabilityInterstitial from 'pages/SuitabilityInterstitial'
import SuitabilityQuestions from 'pages/SuitabilityQuestions'
import { OneTimeDeposit } from 'pages/Transfer/OneTime'
import { RecurringDeposit } from 'pages/Transfer/Recurring'
import Welcome from 'pages/Welcome'
import PageViewedAnalytics from 'utils/analytics'
import FlowProvider from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelProvider } from 'utils/FunnelContext'
import ActivateExperiments from 'utils/Optimizely/Activate'
import FeatureFlagRedirect from 'utils/Optimizely/FeatureFlagRoute'
import { dynamicPricingScreen, maintenanceScreenWeb } from 'utils/Optimizely/features'
import { PrivateRoute } from 'utils/PrivateRoute'
import ScrollToTop from 'utils/scrollToTop'
import SetPageTitle from 'utils/setPageTitle'
import { TieredRegistrationProvider } from 'utils/TieredRegistration'

import MOCSuccess from './pages/MOC/Success'

type InnerProps = WithOptimizelyProps

const MainRouter: React.FC<InnerProps> = ({ optimizely }) => {
  const maintenanceScreenWebIsEnabled = optimizely.isFeatureEnabled(maintenanceScreenWeb)
  const dynamicScreenIsEnabled = optimizely.isFeatureEnabled(dynamicPricingScreen)

  return (
    <BrowserRouter>
      <FunnelProvider>
        <FlowProvider>
          <TieredRegistrationProvider>
            <FeatureFlagRedirect>
              <SetPageTitle />
              <PageViewedAnalytics />
              <ActivateExperiments />

              <ScrollToTop>
                <Switch>
                  <Route
                    path={FlowRoute.Welcome}
                    exact={true}
                    component={maintenanceScreenWebIsEnabled ? Maintenance : Welcome}
                  />
                  <Route
                    path={FlowRoute.Signup}
                    component={maintenanceScreenWebIsEnabled ? Maintenance : Signup}
                  />

                  <Route path={FlowRoute.DirectToMobileLander} component={DirectToMobileLander} />
                  {!dynamicScreenIsEnabled && (
                    <Route path="/select-tier" exact={true} component={SelectTier} />
                  )}
                  <Route
                    path={FlowRoute.DirectBankIntegrationRedirect}
                    component={DirectBankIntegrationRedirect}
                  />

                  <Route path={FlowRoute.PlaidLinkRedirect} component={PlaidLinkRedirect} />

                  <PrivateRoute path={FlowRoute.ShippingAddress} component={ShippingAddress} />
                  <PrivateRoute path={FlowRoute.Occupation} component={Occupation} />
                  <PrivateRoute exact path={FlowRoute.SignYourCard} component={SignYourCard} />

                  {/* SignaturePhone needs to be a public route! */}
                  <Route path={`${FlowRoute.SignYourCard}/:id`} component={SignaturePhone} />

                  <PrivateRoute path={FlowRoute.CardOrderReview} component={CardOrderReview} />

                  <PrivateRoute path={FlowRoute.Funding} component={Funding} />
                  <PrivateRoute path={FlowRoute.Analyzing} component={Analyzing} />
                  <PrivateRoute path={FlowRoute.ContactInfo} component={ContactInfo} />
                  <PrivateRoute path={FlowRoute.Institutions} component={Institutions} />

                  <PrivateRoute path={FlowRoute.RecurringDeposit} component={RecurringDeposit} />
                  <PrivateRoute path={FlowRoute.OneTimeDeposit} component={OneTimeDeposit} />

                  {/* Direct Bank Integration */}
                  <PrivateRoute
                    path={FlowRoute.DirectBankIntegration}
                    component={DirectBankIntegration}
                  />

                  {/* Plaid Flow */}
                  <PrivateRoute path={FlowRoute.BankLinkSuccess} component={BankLinkSuccess} />
                  <PrivateRoute
                    path={FlowRoute.ChooseFundingSource}
                    component={ChooseFundingSource}
                  />
                  <PrivateRoute path={FlowRoute.CryptoLander} component={CryptoInterstitial} />
                  <PrivateRoute
                    path={FlowRoute.InvestInterstitial}
                    component={InvestInterstitial}
                  />
                  <PrivateRoute path={FlowRoute.InvestorQuestions} component={InvestorQuestions} />
                  <PrivateRoute path={FlowRoute.MFASetup} component={MFASetup} />
                  <PrivateRoute path={FlowRoute.PersonalInfo} component={PersonalInfo} />
                  <PrivateRoute path={FlowRoute.PortfolioOverview} component={PortfolioOverview} />
                  <PrivateRoute
                    path={FlowRoute.SelectPortfolioTheme}
                    component={SelectPortfolioTheme}
                  />
                  <PrivateRoute path={FlowRoute.PreviewPortfolio} component={PreviewPortfolio} />
                  <PrivateRoute path={FlowRoute.ESGEducationFlow} component={ESGEducationFlow} />
                  <PrivateRoute path={FlowRoute.AboutESG} component={AboutESG} />
                  <PrivateRoute path={FlowRoute.AboutPortfolio} component={AboutPortfolio} />
                  <PrivateRoute path={FlowRoute.Prefund} component={Prefund} />
                  <PrivateRoute
                    path={FlowRoute.PricingInterstitial}
                    component={PricingInterstitial}
                  />
                  <PrivateRoute
                    path={FlowRoute.SecondChanceBankLink}
                    component={SecondChanceBankLink}
                  />
                  <PrivateRoute
                    path={FlowRoute.SpendOrderConfirmation}
                    component={SpendOrderConfirmation}
                  />
                  <PrivateRoute
                    path={FlowRoute.RecurringInvestment}
                    component={RecurringInvestment}
                  />
                  <PrivateRoute path={FlowRoute.OneTimeInvestment} component={OneTimeInvestment} />
                  <PrivateRoute path={FlowRoute.ReferralReward} component={ReferralReward} />
                  <PrivateRoute
                    path={FlowRoute.ReferralDisclosure}
                    component={ReferralDisclosure}
                  />
                  <PrivateRoute
                    path={FlowRoute.RoundUpsInterstitial}
                    component={RoundUpsInterstitial}
                  />
                  <PrivateRoute path={FlowRoute.RoundUpsSetup} component={RoundUpsSetup} />
                  <PrivateRoute path={FlowRoute.RoundUpsFunding} component={RoundUpsFunding} />
                  <PrivateRoute path={FlowRoute.RoundUpsPast} component={RoundUpsPast} />
                  <PrivateRoute
                    path={FlowRoute.SpendCoreInterstitial}
                    component={SpendCoreInterstitial}
                  />
                  <PrivateRoute path={FlowRoute.SSN} component={SSN} />
                  <PrivateRoute path={FlowRoute.Success} component={Success} />
                  <PrivateRoute path={FlowRoute.MOCSuccess} component={MOCSuccess} />
                  <PrivateRoute
                    path={FlowRoute.SuitabilityQuestions}
                    component={SuitabilityQuestions}
                  />
                  <PrivateRoute
                    path={FlowRoute.ManualFundingSource}
                    component={ManualFundingSource}
                  />
                  <PrivateRoute path={FlowRoute.MOCInterstitial} component={MOCInterstitial} />
                  <PrivateRoute
                    path={FlowRoute.MOCProgressStepOne}
                    component={MOCProgressStepOne}
                  />
                  <PrivateRoute
                    path={FlowRoute.MOCProgressStepTwo}
                    component={MOCProgressStepTwo}
                  />
                  <PrivateRoute
                    path={FlowRoute.MOCReferralReminder}
                    component={MOCReferralReminder}
                  />
                  <PrivateRoute
                    path={FlowRoute.MOCProgressStepThree}
                    component={MOCProgressStepThree}
                  />
                  <PrivateRoute
                    path={FlowRoute.SuitabilityInterstitial}
                    component={SuitabilityInterstitial}
                  />
                  <PrivateRoute
                    path={FlowRoute.LaterSuitabilityQuestions}
                    component={LaterSuitabilityQuestions}
                  />
                  <PrivateRoute
                    path={FlowRoute.LaterIraRecommendation}
                    component={LaterIraRecommendation}
                  />
                  <PrivateRoute
                    path={FlowRoute.PortfolioRecommendation}
                    component={PortfolioRecommendation}
                  />

                  <Redirect
                    to={{
                      pathname: FlowRoute.Signup,
                      state: { pageNotFound: true },
                    }}
                  />
                </Switch>
              </ScrollToTop>
            </FeatureFlagRedirect>
          </TieredRegistrationProvider>
        </FlowProvider>
      </FunnelProvider>
    </BrowserRouter>
  )
}

export default withOptimizely(MainRouter)
